import {
  DialogActionTrigger,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { useDeleteProgramAction } from '@/hooks/actions/use-delete-program-action';
import { useProgramDeletionCheckQuery } from '@/hooks/queries/use-program-deletion-check-query';
import { Box, Button, Input, Spinner, Stack, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DeletionImpactSummary from './DeletionImpactSummary';

interface ProgramDeletionDialogProps {
  isOpen: boolean;
  programId: string;
  programName: string;
  onClose: () => void;
}

enum DeletionStep {
  VALIDATION = 'validation',
  CONFIRMATION = 'confirmation',
}

const ProgramDeletionDialog: React.FC<ProgramDeletionDialogProps> = ({
  isOpen,
  programId,
  programName,
  onClose,
}) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<DeletionStep>(DeletionStep.VALIDATION);
  const [confirmationText, setConfirmationText] = useState('');
  const navigate = useNavigate();

  const {
    data: checkData,
    isLoading: isCheckingDeletion,
    error: checkError,
    refetch: refetchCheck,
  } = useProgramDeletionCheckQuery({
    programId,
    enabled: isOpen,
  });

  const deleteProgramMutation = useDeleteProgramAction();

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep(DeletionStep.VALIDATION);
      setConfirmationText('');
      refetchCheck();
    }
  }, [isOpen, refetchCheck]);

  const isConfirmationValid = confirmationText.toLowerCase() === 'delete';
  const canProceedToConfirmation = checkData && !isCheckingDeletion;

  const handleStepOne = () => {
    if (canProceedToConfirmation) {
      setStep(DeletionStep.CONFIRMATION);
    }
  };

  const handleFinalDeletion = async () => {
    if (!isConfirmationValid) return;

    try {
      await deleteProgramMutation.mutateAsync(programId);
      onClose();
      // Navigate to dashboard after successful deletion
      navigate('/dashboard');
    } catch (error) {
      // Error is handled by the mutation's onError callback
      console.error('Deletion failed:', error);
    }
  };

  const handleClose = () => {
    if (!deleteProgramMutation.isPending) {
      onClose();
    }
  };

  const renderStepOne = () => (
    <>
      <DialogHeader>
        <Box>
          <DialogTitle>{t('programs.deletion.dialogTitle', { programName })}</DialogTitle>
          <DialogDescription>{t('programs.deletion.dialogDescription')}</DialogDescription>
        </Box>
      </DialogHeader>
      <DialogBody>
        <VStack gap={4} align="stretch">
          {isCheckingDeletion && (
            <Stack gap={4} py={6} align="center">
              <Spinner size="lg" color="dark" />
              <Text fontSize="sm" color="dark">
                {t('programs.deletion.checkingRequirements')}
              </Text>
            </Stack>
          )}

          {checkError && (
            <Text color="red.500" fontSize="sm">
              {t('programs.deletion.checkError', { message: checkError.message })}
            </Text>
          )}

          {checkData && <DeletionImpactSummary checkData={checkData} />}
        </VStack>
      </DialogBody>
      <DialogFooter>
        <DialogActionTrigger asChild>
          <Button variant="outline" onClick={handleClose}>
            {t('programs.deletion.actions.cancelButton')}
          </Button>
        </DialogActionTrigger>
        <Button
          colorPalette="red"
          onClick={handleStepOne}
          disabled={!canProceedToConfirmation || isCheckingDeletion}
        >
          {t('programs.deletion.actions.confirmToDelete')}
        </Button>
      </DialogFooter>
    </>
  );

  const renderStepTwo = () => (
    <>
      <DialogHeader>
        <DialogTitle>{t('programs.deletion.confirmStep.title')}</DialogTitle>
      </DialogHeader>
      <DialogBody>
        <VStack gap={4} align="stretch">
          <Box p={3} bg="red.50" rounded="md">
            <Text fontSize="sm" color="red.700" fontWeight="medium">
              {t('programs.deletion.confirmStep.warningText', { programName })}
            </Text>
            <Text fontSize="sm" color="red.700" mt={1}>
              {t('programs.deletion.confirmStep.cannotUndoText')}
            </Text>
          </Box>

          <Field label={t('programs.deletion.confirmStep.confirmLabel', { programName })} required>
            <Input
              value={confirmationText}
              onChange={(e) => setConfirmationText(e.target.value)}
              placeholder={t('programs.deletion.confirmStep.confirmPlaceholder')}
            />
          </Field>
        </VStack>
      </DialogBody>
      <DialogFooter>
        <Button
          variant="outline"
          onClick={() => setStep(DeletionStep.VALIDATION)}
          disabled={deleteProgramMutation.isPending}
        >
          {t('programs.deletion.actions.backButton')}
        </Button>
        <Button
          colorPalette="red"
          onClick={handleFinalDeletion}
          disabled={!isConfirmationValid}
          loading={deleteProgramMutation.isPending}
          loadingText={t('programs.deletion.actions.deletingButton')}
        >
          {t('programs.deletion.actions.deleteForever')}
        </Button>
      </DialogFooter>
    </>
  );

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={handleClose}
      closeOnInteractOutside={false}
      closeOnEscape={!deleteProgramMutation.isPending}
    >
      <DialogContent maxW="lg" rounded="xl">
        {step === DeletionStep.VALIDATION ? renderStepOne() : renderStepTwo()}
      </DialogContent>
    </DialogRoot>
  );
};

export default ProgramDeletionDialog;
